import { create } from 'zustand';

export type NoticeKind = 'error' | 'success';

export interface Notice {
  id: string;
  kind: NoticeKind;
  /** The word on the plate: ERROR, REWARD, DONE… */
  kicker: string;
  /** The headline, in the slab's own lettering. */
  title: string;
  /** The line beneath it. */
  subtitle: string;
}

interface NoticeState {
  current: Notice | null;
  show(input: Omit<Notice, 'id'>): void;
  dismiss(): void;
}

/**
 * The notice: one painted slab, in the middle of the app, for the moments that
 * deserve to stop everything — a refusal, a ban, a failure; a reward, a
 * completed task, an approval.
 *
 * One at a time on purpose. Two slabs stacked would be two things shouting, and
 * the second would bury the first: a new notice replaces whatever was there.
 */
export const useNoticeStore = create<NoticeState>((set) => ({
  current: null,
  show: (input) =>
    set({ current: { id: `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, ...input } }),
  dismiss: () => set({ current: null }),
}));

/**
 * Imperative helpers, so a mutation can raise one without reaching for a hook.
 * Their fixed words (the kicker, a default line) are catalogue keys, which the
 * Notice turns into the reader's language; what callers pass is already theirs.
 */
export const notice = {
  error: (title: string, subtitle?: string) =>
    useNoticeStore.getState().show({
      kind: 'error',
      kicker: 'notice.kicker.error',
      title,
      subtitle: subtitle ?? 'common.somethingWrong',
    }),
  /** A refusal that is not a fault: not enough balance, a locked miner, a cap. */
  blocked: (title: string, subtitle?: string) =>
    useNoticeStore.getState().show({
      kind: 'error',
      kicker: 'notice.kicker.denied',
      title,
      subtitle: subtitle ?? 'notice.tryAgainSoon',
    }),
  banned: (title: string, subtitle?: string) =>
    useNoticeStore.getState().show({
      kind: 'error',
      kicker: 'notice.kicker.banned',
      title,
      subtitle: subtitle ?? 'ban.support',
    }),
  success: (title: string, subtitle?: string, kicker = 'notice.kicker.done') =>
    useNoticeStore.getState().show({ kind: 'success', kicker, title, subtitle: subtitle ?? '' }),
  reward: (title: string, subtitle = 'notice.addedToBalance') =>
    useNoticeStore.getState().show({ kind: 'success', kicker: 'notice.kicker.reward', title, subtitle }),
};
